'use client';

import { ChangeEvent, useRef, useState } from 'react';
import { Modal } from '@wishket/design-system';

import { useDeviceCheck, useProfile } from '@/shared/hooks';
import { ProfileImageForm } from '@/features/Profile';
import { usePatchProfileMutation } from '@/entities/profile/models/profile.models';
import { UIOverlayModalProps } from '@/shared/types';

const MAX_FILE_SIZE = 8 * 1024 * 1024; // 8MB

interface ProfileImageChangeProps extends UIOverlayModalProps {
  onClose: () => void;
}

const ProfileImageChange = ({ isOpen, onClose }: ProfileImageChangeProps) => {
  const { profile } = useProfile();
  const defaultImage = profile?.imgSrc;
  const [imagePreview, setImagePreview] = useState<string | undefined>(
    defaultImage
  );
  const imageFile = useRef<File | null>(null);
  const fileUploadInputRef = useRef<HTMLInputElement>(null);
  const isImageChanged = defaultImage !== imagePreview;

  const { mutate } = usePatchProfileMutation();

  const clearImageURL = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
  };

  const uploadImage = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    const isFileSelected = files && files.length > 0;

    if (!isFileSelected) return;

    const uploadedImageFile = files[0];

    if (uploadedImageFile.size > MAX_FILE_SIZE) {
      alert('파일은 8MB 이하만 가능합니다.');
      event.target.value = '';
      return;
    }
    clearImageURL();

    const uploadedImagePreview = URL.createObjectURL(uploadedImageFile);
    setImagePreview(uploadedImagePreview);
    imageFile.current = uploadedImageFile;
  };

  const openImageSelector = () => {
    fileUploadInputRef.current?.click();
  };

  const deleteImage = () => {
    clearImageURL();
    setImagePreview(undefined);
    imageFile.current = null;
    if (fileUploadInputRef.current) {
      fileUploadInputRef.current.value = '';
    }
  };

  const submitImage = () => {
    if (!profile || !profile.yozmitProfileId) return;

    if (!isImageChanged) return handleClose();

    mutate(
      {
        image: imageFile.current || '', // 빈 문자열 -> 이미지 제거 기능
        yozmitProfileId: profile.yozmitProfileId,
      },
      {
        onSuccess: () => {
          handleClose();
        },
        onError: () => {
          alert('잠시 후 다시 시도해주세요.');
        },
      }
    );
  };

  const handleClose = () => {
    clearImageURL();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <input
        ref={fileUploadInputRef}
        accept=".jpg,.jpeg,.png"
        type="file"
        className="hidden"
        onChange={uploadImage}
      />
      <ProfileImageForm
        profileImgSrc={imagePreview}
        onUpload={openImageSelector}
        onDelete={deleteImage}
        onClose={handleClose}
        onSubmit={submitImage}
      />
    </>
  );
};

const ProfileImageChangeModal = ({
  isOpen,
  onClose,
  rootId,
}: ProfileImageChangeProps) => {
  const { isDesktop } = useDeviceCheck();

  return (
    <Modal
      variant={isDesktop ? 'default' : 'full'}
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      isPreventScroll
      rootId={rootId}
    >
      <ProfileImageChange isOpen={isOpen} onClose={onClose} />
    </Modal>
  );
};

export default ProfileImageChangeModal;
